import React, { useState } from "react";
import { Check, ArrowRight, RotateCcw, Ruler } from "lucide-react";
import { StaffViewer } from "@/components/StaffViewer";
import { SoundToggle } from "@/components/SoundToggle";
import { playResult, playFanfare } from "@/lib/feedbackSounds";

// The eight "From ___ to ___ is a ___" examples (treble then bass clef).
export const TONE_EXAMPLES = [
  { clef: "treble", ps: ["F4", "G4"] }, { clef: "treble", ps: ["E4", "F4"] }, { clef: "treble", ps: ["Bb4", "A4"] }, { clef: "treble", ps: ["F#4", "G#4"] },
  { clef: "bass", ps: ["B2", "C3"] }, { clef: "bass", ps: ["F3", "F#3"] }, { clef: "bass", ps: ["Eb3", "F3"] }, { clef: "bass", ps: ["Eb3", "Db3"] },
];
const SEMI = { C: 0, D: 2, E: 4, F: 5, G: 7, A: 9, B: 11 };
const ACC = { "": 0, "#": 1, b: -1 };
const midi = (p) => Number(p.slice(-1)) * 12 + SEMI[p[0]] + ACC[p.slice(1, -1)];
const nameOf = (p) => `${p[0]}${{ "#": "♯", b: "♭" }[p.slice(1, -1)] || ""}`;
const sizeOf = (ps) => (Math.abs(midi(ps[0]) - midi(ps[1])) === 1 ? "semitone" : "tone");
const why = (ps) => {
  const [a, b] = ps.map(nameOf);
  if (a[0] === b[0]) return `${a} to ${b}: the same letter moved to the very next key, so it's a semitone.`;
  return sizeOf(ps) === "semitone" ? `${a} to ${b}: next-door keys with nothing in between, so it's a semitone.` : `${a} to ${b}: one key lies in between, so it's a tone (two semitones).`;
};
const LETTERS = ["C", "D", "E", "F", "G", "A", "B"];
const ACCS = [["", "none"], ["#", "♯"], ["b", "♭"]];
const blank = () => ({ from: { l: null, a: "" }, to: { l: null, a: "" }, size: null });

const NamePicker = ({ label, value, onChange, locked, testId }) => {
  const chip = (on) => `min-w-9 rounded-full border-2 px-2.5 py-1 text-sm font-bold transition-colors disabled:opacity-70 ${on ? "border-primary bg-primary text-primary-foreground" : "border-border hover:border-primary"}`;
  return (
    <div className="mt-3" data-testid={testId}>
      <p className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">{label}</p>
      <div className="mt-1 flex flex-wrap gap-1.5">
        {LETTERS.map((l) => <button key={l} type="button" disabled={locked} onClick={() => onChange({ ...value, l })} className={chip(value.l === l)} data-testid={`${testId}-${l}`} aria-pressed={value.l === l}>{l}</button>)}
      </div>
      <div className="mt-1.5 flex flex-wrap gap-1.5">
        {ACCS.map(([a, t]) => <button key={t} type="button" disabled={locked} onClick={() => onChange({ ...value, a })} className={chip(value.a === a)} data-testid={`${testId}-acc-${t === "none" ? "none" : a === "#" ? "sharp" : "flat"}`} aria-pressed={value.a === a}>{t}</button>)}
      </div>
    </div>
  );
};

// Lesson 5: name both notes, then say whether the distance is a tone or a semitone.
export const ToneSemitoneQuiz = ({ testId = "tone-quiz" }) => {
  const [i, setI] = useState(0);
  const [ans, setAns] = useState(blank);
  const [checked, setChecked] = useState(null);
  const [score, setScore] = useState(0);
  const [done, setDone] = useState(false);
  const ex = TONE_EXAMPLES[i], total = TONE_EXAMPLES.length;
  const ready = ans.from.l && ans.to.l && ans.size;
  const right = (v, p) => v.l === p[0] && v.a === p.slice(1, -1);
  const check = () => {
    const r = { from: right(ans.from, ex.ps[0]), to: right(ans.to, ex.ps[1]), size: ans.size === sizeOf(ex.ps) };
    const ok = r.from && r.to && r.size;
    setChecked({ ...r, ok }); playResult(ok);
    if (ok) setScore((s) => s + 1);
  };
  const next = () => {
    if (i === total - 1) { setDone(true); if (score === total) playFanfare(0.2, "Tones & semitones quiz"); return; }
    setI(i + 1); setAns(blank()); setChecked(null);
  };
  const restart = () => { setI(0); setAns(blank()); setChecked(null); setScore(0); setDone(false); };
  const mark = (ok) => (checked ? (ok ? "text-emerald-600 dark:text-emerald-400" : "text-red-600 dark:text-red-400") : "");

  return (
    <div className="mt-6 rounded-2xl border border-border bg-card p-4 sm:p-5" data-testid={testId}>
      <div className="flex flex-wrap items-center justify-between gap-2">
        <p className="flex items-center gap-2 font-mono text-xs uppercase tracking-widest text-primary"><Ruler size={14} /> Tones &amp; semitones quiz</p>
        <SoundToggle testId={`${testId}-sound-toggle`} className="ml-auto" />
        <p className="text-xs text-muted-foreground" data-testid={`${testId}-progress`}>{done ? `${total} of ${total}` : `Example ${i + 1} of ${total}`} · Score {score}</p>
      </div>
      {done ? (
        <div className="mt-4 text-center">
          <p className="text-base font-semibold" data-testid={`${testId}-result`}>{score === total ? `Perfect! ${score} out of ${total} — you earned a star.` : `You got ${score} out of ${total} completely right.`}</p>
          <button type="button" onClick={restart} data-testid={`${testId}-restart`} className="mt-3 inline-flex items-center gap-1.5 rounded-full bg-primary px-5 py-2 text-sm font-bold text-primary-foreground"><RotateCcw size={14} /> Play again</button>
        </div>
      ) : (
        <>
          <p className="mt-3 text-sm text-muted-foreground">Name both notes (letter, then ♯ or ♭ if it has one), then choose tone or semitone. Tap a note to hear it.</p>
          <div className={`mt-3 rounded-xl border-2 bg-background/60 p-2 ${!checked ? "border-border" : checked.ok ? "border-emerald-500" : "border-red-500"}`} data-testid={`${testId}-card`} data-clef={ex.clef}>
            <p className="font-mono text-[11px] uppercase tracking-widest text-primary">{ex.clef} clef</p>
            <div className="mx-auto max-w-sm"><StaffViewer clef={ex.clef} notes={ex.ps.map((p) => ({ pitch: p, label: "", duration: "whole" }))} width={300} testId={`${testId}-staff`} /></div>
          </div>
          <NamePicker label="From" value={ans.from} onChange={(from) => setAns({ ...ans, from })} locked={!!checked} testId={`${testId}-from`} />
          <NamePicker label="to" value={ans.to} onChange={(to) => setAns({ ...ans, to })} locked={!!checked} testId={`${testId}-to`} />
          <div className="mt-3">
            <p className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">is a</p>
            <div className="mt-1 flex gap-2">
              {["tone", "semitone"].map((s) => (
                <button key={s} type="button" disabled={!!checked} onClick={() => setAns({ ...ans, size: s })} aria-pressed={ans.size === s} data-testid={`${testId}-${s}`}
                  className={`rounded-full border-2 px-4 py-1.5 text-sm font-bold capitalize transition-colors disabled:opacity-70 ${ans.size === s ? "border-primary bg-primary text-primary-foreground" : "border-border hover:border-primary"}`}>{s}</button>
              ))}
            </div>
          </div>
          {!checked ? (
            <div className="mt-4 flex justify-center">
              <button type="button" onClick={check} disabled={!ready} data-testid={`${testId}-check`} className="inline-flex items-center gap-1.5 rounded-full bg-primary px-5 py-2 text-sm font-bold text-primary-foreground disabled:opacity-50"><Check size={15} /> Check</button>
            </div>
          ) : (
            <div className="mt-4 space-y-2" data-testid={`${testId}-feedback`}>
              <p className="text-sm font-semibold">
                From <span className={mark(checked.from)} data-testid={`${testId}-answer-from`}>{nameOf(ex.ps[0])}</span> to <span className={mark(checked.to)} data-testid={`${testId}-answer-to`}>{nameOf(ex.ps[1])}</span> is a <span className={mark(checked.size)} data-testid={`${testId}-answer-size`}>{sizeOf(ex.ps)}</span>.
              </p>
              <div className="flex flex-wrap items-center justify-between gap-3">
                <p className={`text-sm ${checked.ok ? "text-emerald-600 dark:text-emerald-400" : "text-red-600 dark:text-red-400"}`}>{checked.ok ? "Correct! " : "Not quite. "}{why(ex.ps)}</p>
                <button type="button" onClick={next} data-testid={`${testId}-next`} className="inline-flex items-center gap-1.5 rounded-full bg-primary px-4 py-1.5 text-xs font-bold text-primary-foreground">{i === total - 1 ? "See my score" : "Next"} <ArrowRight size={13} /></button>
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );
};
